/**
 * Plan Context - State management for Corridor/Plan data
 *
 * Uses React Context + useReducer for centralized state.
 * Auto-saves to localStorage when data changes.
 *
 * Structure:
 *   Corridor (saved to DB)
 *   ├── intersections[] - shared base data (names, distances, speeds)
 *   └── plans[] - up to 10 timing variations
 *       └── timing[] - offsets and phases per intersection
 */

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  type ReactNode,
  type Dispatch,
} from 'react';
import type {
  Corridor,
  Intersection,
  IntersectionBase,
  IntersectionTiming,
  TimingPlan,
  CorridorSettings,
  Plan,
  Phase,
} from '../types/plan';
import {
  computeDefaultSettings,
  getActiveIntersections,
  extractBase,
  extractTiming,
  planToCorridor,
  MAX_PLANS_PER_CORRIDOR,
} from '../types/plan';
import { createEmptyPlan, createDefaultIntersection, defaultPhaseDirection } from '../lib/utils';
import {
  normalizeLegacyPlan,
  saveDraftToStorage,
  loadDraftFromStorage,
  getLastPlanId,
  setLastPlanId,
} from '../lib/draft-storage';
import { applyAutoSequence, type SequenceDirection } from '../lib/autoSequence';
import { fetchPlan, fetchPlanList } from '../lib/api';

// ============================================================================
// State Shape
// ============================================================================

interface PlanState {
  corridor: Corridor | null;
  dbId: number | null;      // null = not saved to DB, number = DB row id
  dbIsExample: boolean;     // true when dbId points at a seeded example (not owned): Save must create, not update
  isDirty: boolean;         // unsaved changes since last explicit save
  isLoading: boolean;
  error: string | null;
  selectedIntersectionId: string | null;  // transient UI state, not saved
}

const initialState: PlanState = {
  corridor: null,
  dbId: null,
  dbIsExample: false,
  isDirty: false,
  isLoading: false,
  error: null,
  selectedIntersectionId: null,
};

// ============================================================================
// Actions
// ============================================================================

type PlanAction =
  // Corridor-level actions
  | { type: 'NEW_CORRIDOR'; name?: string }
  | { type: 'LOAD_CORRIDOR'; corridor: Corridor }
  | { type: 'UPDATE_CORRIDOR_NAME'; name: string }
  | { type: 'UPDATE_SETTINGS'; settings: Partial<CorridorSettings> }
  | { type: 'MARK_SAVED' }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'LOAD_DB_CORRIDOR'; corridor: Corridor; dbId: number; isExample?: boolean }
  | { type: 'SET_DB_ID'; dbId: number }
  // Timing plan actions
  | { type: 'ADD_TIMING_PLAN'; name?: string }
  | { type: 'REMOVE_TIMING_PLAN'; planId: string }
  | { type: 'SELECT_TIMING_PLAN'; index: number }
  | { type: 'UPDATE_TIMING_PLAN_NAME'; planId: string; name: string }
  | { type: 'DUPLICATE_TIMING_PLAN'; planId: string }
  | { type: 'REORDER_TIMING_PLANS'; fromIndex: number; toIndex: number }
  // Intersection actions (affect base data shared across plans)
  | { type: 'UPDATE_INTERSECTION_BASE'; id: string; updates: Partial<IntersectionBase> }
  | { type: 'ADD_INTERSECTION'; afterId?: string }
  | { type: 'REMOVE_INTERSECTION'; id: string }
  | { type: 'SELECT_INTERSECTION'; id: string | null }
  // Timing actions (affect only active plan)
  | { type: 'UPDATE_INTERSECTION_TIMING'; id: string; updates: Partial<IntersectionTiming> }
  | { type: 'AUTO_SEQUENCE'; direction: SequenceDirection; smartMode: boolean }
  // Legacy support
  | { type: 'LOAD_PLAN'; plan: Plan };

// ============================================================================
// Helpers
// ============================================================================

/** Create a new empty corridor */
function createEmptyCorridor(name?: string): Corridor {
  const legacyPlan = createEmptyPlan(name);
  return planToCorridor(legacyPlan);
}

/** Create default timing for an intersection */
function createDefaultTiming(intersectionId: string): IntersectionTiming {
  const defaultPhases: Phase[] = [
    { id: 'A', duration: 36, direction: 'NBSB' },
    { id: 'B', duration: 36, direction: 'NB' },
    { id: 'C', duration: 36, direction: 'SB' },
  ];
  return {
    intersectionId,
    offset: 0,
    phases: defaultPhases,
  };
}

/** Backfill missing fields on corridors/plans saved before newer properties existed */
function normalizeCorridor(corridor: Corridor): Corridor {
  let corridorName = corridor.name;

  // Backfill crossStreet from legacy name field
  const needsCrossStreet = corridor.intersections.some(int => !int.crossStreet);
  let intersections = corridor.intersections;

  if (needsCrossStreet && intersections.length > 0) {
    // Try to extract common corridor prefix from "Corridor & CrossStreet" names
    const parts = intersections.map(int => {
      const ampIdx = (int.name || '').indexOf(' & ');
      return ampIdx >= 0
        ? { corridor: int.name.slice(0, ampIdx), cross: int.name.slice(ampIdx + 3) }
        : null;
    });

    const allHaveAmp = parts.every(p => p !== null);
    const commonCorridor = allHaveAmp
      ? parts.every(p => p!.corridor === parts[0]!.corridor) ? parts[0]!.corridor : null
      : null;

    if (commonCorridor) {
      corridorName = commonCorridor;
      intersections = intersections.map((int, i) => ({
        ...int,
        crossStreet: int.crossStreet || parts[i]!.cross,
      }));
    } else {
      intersections = intersections.map(int => ({
        ...int,
        crossStreet: int.crossStreet || int.name || '',
      }));
    }
  }

  // Backfill phase directions in all timing plans
  const plans = corridor.plans.map(plan => ({
    ...plan,
    timing: plan.timing.map(t => ({
      ...t,
      phases: t.phases.map((p, i) => ({
        ...p,
        direction: p.direction ?? defaultPhaseDirection(i),
      })),
    })),
  }));

  return { ...corridor, name: corridorName, intersections, plans };
}

/** Get active timing plan or undefined */
function getActivePlan(corridor: Corridor): TimingPlan | undefined {
  return corridor.plans[corridor.activePlanIndex];
}

// ============================================================================
// Reducer
// ============================================================================

function planReducer(state: PlanState, action: PlanAction): PlanState {
  switch (action.type) {
    // ========================================================================
    // Corridor-level actions
    // ========================================================================

    case 'NEW_CORRIDOR':
      return {
        ...state,
        corridor: createEmptyCorridor(action.name),
        dbId: null,
        dbIsExample: false,
        isDirty: true,
        error: null,
      };

    case 'LOAD_CORRIDOR':
      return {
        ...state,
        corridor: normalizeCorridor(action.corridor),
        dbId: null,
        dbIsExample: false,
        isDirty: false,
        error: null,
        selectedIntersectionId: action.corridor.intersections.length >= 2
          ? action.corridor.intersections[1].id
          : null,
      };

    case 'UPDATE_CORRIDOR_NAME':
      if (!state.corridor) return state;
      return {
        ...state,
        corridor: {
          ...state.corridor,
          name: action.name,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };

    case 'UPDATE_SETTINGS':
      if (!state.corridor) return state;
      return {
        ...state,
        corridor: {
          ...state.corridor,
          settings: { ...state.corridor.settings, ...action.settings },
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };

    case 'MARK_SAVED':
      return { ...state, isDirty: false };

    case 'SET_ERROR':
      return { ...state, error: action.error, isLoading: false };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'SET_LOADING':
      return { ...state, isLoading: action.isLoading };

    case 'LOAD_DB_CORRIDOR':
      setLastPlanId(action.dbId);
      return {
        ...state,
        corridor: normalizeCorridor(action.corridor),
        dbId: action.dbId,
        dbIsExample: action.isExample ?? false,
        isDirty: false,
        error: null,
        selectedIntersectionId: action.corridor.intersections.length >= 2
          ? action.corridor.intersections[1].id
          : null,
      };

    case 'SET_DB_ID':
      return { ...state, dbId: action.dbId, dbIsExample: false };

    // ========================================================================
    // Timing Plan actions
    // ========================================================================

    case 'ADD_TIMING_PLAN': {
      if (!state.corridor) return state;
      if (state.corridor.plans.length >= MAX_PLANS_PER_CORRIDOR) return state;

      const planNumber = state.corridor.plans.length + 1;
      const newPlan: TimingPlan = {
        id: crypto.randomUUID(),
        name: action.name || `Plan ${planNumber}`,
        timing: state.corridor.intersections.map(int => createDefaultTiming(int.id)),
      };

      return {
        ...state,
        corridor: {
          ...state.corridor,
          plans: [...state.corridor.plans, newPlan],
          activePlanIndex: state.corridor.plans.length, // Switch to new plan
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    case 'REMOVE_TIMING_PLAN': {
      if (!state.corridor) return state;
      if (state.corridor.plans.length <= 1) return state; // Keep at least one plan

      const planIndex = state.corridor.plans.findIndex(p => p.id === action.planId);
      if (planIndex < 0) return state;

      const newPlans = state.corridor.plans.filter(p => p.id !== action.planId);
      let newActiveIndex = state.corridor.activePlanIndex;

      // Adjust active index if needed
      if (planIndex <= state.corridor.activePlanIndex) {
        newActiveIndex = Math.max(0, state.corridor.activePlanIndex - 1);
      }
      if (newActiveIndex >= newPlans.length) {
        newActiveIndex = newPlans.length - 1;
      }

      return {
        ...state,
        corridor: {
          ...state.corridor,
          plans: newPlans,
          activePlanIndex: newActiveIndex,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    case 'SELECT_TIMING_PLAN': {
      if (!state.corridor) return state;
      const index = Math.max(0, Math.min(action.index, state.corridor.plans.length - 1));
      if (index === state.corridor.activePlanIndex) return state;

      return {
        ...state,
        corridor: {
          ...state.corridor,
          activePlanIndex: index,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    case 'UPDATE_TIMING_PLAN_NAME': {
      if (!state.corridor) return state;

      const plans = state.corridor.plans.map(p =>
        p.id === action.planId ? { ...p, name: action.name } : p
      );

      return {
        ...state,
        corridor: {
          ...state.corridor,
          plans,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    case 'DUPLICATE_TIMING_PLAN': {
      if (!state.corridor) return state;
      if (state.corridor.plans.length >= MAX_PLANS_PER_CORRIDOR) return state;

      const sourcePlan = state.corridor.plans.find(p => p.id === action.planId);
      if (!sourcePlan) return state;

      const newPlan: TimingPlan = {
        id: crypto.randomUUID(),
        name: `${sourcePlan.name} (copy)`,
        timing: sourcePlan.timing.map(t => ({
          ...t,
          phases: t.phases.map(p => ({ ...p })),
        })),
      };

      return {
        ...state,
        corridor: {
          ...state.corridor,
          plans: [...state.corridor.plans, newPlan],
          activePlanIndex: state.corridor.plans.length, // Switch to new plan
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    case 'REORDER_TIMING_PLANS': {
      if (!state.corridor) return state;
      const { fromIndex, toIndex } = action;
      const plans = [...state.corridor.plans];
      if (fromIndex < 0 || fromIndex >= plans.length || toIndex < 0 || toIndex >= plans.length) return state;
      if (fromIndex === toIndex) return state;

      const [moved] = plans.splice(fromIndex, 1);
      plans.splice(toIndex, 0, moved);

      // Keep the same plan active by tracking its new index
      let newActiveIndex = state.corridor.activePlanIndex;
      if (state.corridor.activePlanIndex === fromIndex) {
        newActiveIndex = toIndex;
      } else if (fromIndex < state.corridor.activePlanIndex && toIndex >= state.corridor.activePlanIndex) {
        newActiveIndex--;
      } else if (fromIndex > state.corridor.activePlanIndex && toIndex <= state.corridor.activePlanIndex) {
        newActiveIndex++;
      }

      return {
        ...state,
        corridor: {
          ...state.corridor,
          plans,
          activePlanIndex: newActiveIndex,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    // ========================================================================
    // Intersection Base actions (shared across all plans)
    // ========================================================================

    case 'UPDATE_INTERSECTION_BASE': {
      if (!state.corridor) return state;

      const intersections = state.corridor.intersections.map(int =>
        int.id === action.id ? { ...int, ...action.updates } : int
      );

      return {
        ...state,
        corridor: {
          ...state.corridor,
          intersections,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    case 'ADD_INTERSECTION': {
      if (!state.corridor) return state;

      const currentIntersections = state.corridor.intersections;
      const newSeqNo = currentIntersections.length + 1;
      const tempIntersection = createDefaultIntersection(newSeqNo);

      // Extract base data from the default intersection
      const newBase: IntersectionBase = extractBase(tempIntersection);

      let intersections: IntersectionBase[];
      if (action.afterId) {
        const idx = currentIntersections.findIndex(i => i.id === action.afterId);
        if (idx >= 0) {
          intersections = [
            ...currentIntersections.slice(0, idx + 1),
            newBase,
            ...currentIntersections.slice(idx + 1),
          ];
          intersections = intersections.map((int, i) => ({
            ...int,
            sequenceNo: i + 1,
          }));
        } else {
          intersections = [...currentIntersections, newBase];
        }
      } else {
        intersections = [...currentIntersections, newBase];
      }

      // Add timing for new intersection to all plans
      const newTiming = createDefaultTiming(newBase.id);
      const plans = state.corridor.plans.map(plan => ({
        ...plan,
        timing: [...plan.timing, newTiming],
      }));

      return {
        ...state,
        corridor: {
          ...state.corridor,
          intersections,
          plans,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    case 'REMOVE_INTERSECTION': {
      if (!state.corridor) return state;

      let intersections = state.corridor.intersections.filter(
        int => int.id !== action.id
      );
      intersections = intersections.map((int, i) => ({
        ...int,
        sequenceNo: i + 1,
      }));

      // Remove timing for deleted intersection from all plans
      const plans = state.corridor.plans.map(plan => ({
        ...plan,
        timing: plan.timing.filter(t => t.intersectionId !== action.id),
      }));

      return {
        ...state,
        corridor: {
          ...state.corridor,
          intersections,
          plans,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    case 'SELECT_INTERSECTION':
      return { ...state, selectedIntersectionId: action.id };

    // ========================================================================
    // Intersection Timing actions (affect only active plan)
    // ========================================================================

    case 'UPDATE_INTERSECTION_TIMING': {
      if (!state.corridor) return state;

      const activePlan = getActivePlan(state.corridor);
      if (!activePlan) return state;

      const updatedTiming = activePlan.timing.map(t =>
        t.intersectionId === action.id ? { ...t, ...action.updates } : t
      );

      const updatedPlan = { ...activePlan, timing: updatedTiming };
      const plans = state.corridor.plans.map((p, i) =>
        i === state.corridor!.activePlanIndex ? updatedPlan : p
      );

      // Recompute settings if phases changed
      const settings = action.updates.phases
        ? computeDefaultSettings(getActiveIntersections({ ...state.corridor, plans }), state.corridor.settings)
        : state.corridor.settings;

      return {
        ...state,
        corridor: {
          ...state.corridor,
          plans,
          settings,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    case 'AUTO_SEQUENCE': {
      if (!state.corridor) return state;

      const activeIntersections = getActiveIntersections(state.corridor);
      if (activeIntersections.length < 2) return state;

      const updatedIntersections = applyAutoSequence(
        activeIntersections,
        action.direction,
        action.smartMode
      );

      // Extract updated timing and update only the active plan
      const activePlan = getActivePlan(state.corridor);
      if (!activePlan) return state;

      const updatedTiming = activePlan.timing.map(t => {
        const updated = updatedIntersections.find(int => int.id === t.intersectionId);
        return updated ? extractTiming(updated) : t;
      });

      const updatedPlan = { ...activePlan, timing: updatedTiming };
      const plans = state.corridor.plans.map((p, i) =>
        i === state.corridor!.activePlanIndex ? updatedPlan : p
      );

      return {
        ...state,
        corridor: {
          ...state.corridor,
          plans,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    }

    // ========================================================================
    // Legacy support - convert Plan to Corridor
    // ========================================================================

    case 'LOAD_PLAN': {
      const normalizedPlan = normalizeLegacyPlan(action.plan);
      const corridor = planToCorridor(normalizedPlan);
      return {
        ...state,
        corridor: normalizeCorridor(corridor),
        dbId: null,
        isDirty: false,
        error: null,
        selectedIntersectionId: corridor.intersections.length >= 2
          ? corridor.intersections[1].id
          : null,
      };
    }

    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

interface PlanContextValue {
  state: PlanState;
  dispatch: Dispatch<PlanAction>;
  /** Convenience: get merged intersections for rendering */
  activeIntersections: Intersection[];
  /** Convenience: get active timing plan */
  activePlan: TimingPlan | undefined;
}

const PlanContext = createContext<PlanContextValue | null>(null);

/** Auto-load last opened plan, or first example plan as default */
async function autoLoadPlan(dispatch: Dispatch<PlanAction>) {
  try {
    const lastId = getLastPlanId();
    if (lastId) {
      try {
        const record = await fetchPlan(Number(lastId));
        dispatch({ type: 'LOAD_DB_CORRIDOR', corridor: record.plan_data, dbId: record.id, isExample: record.is_example ?? false });
        return;
      } catch { /* plan may have been deleted, fall through */ }
    }
    const list = await fetchPlanList();
    if (list.length > 0) {
      // Own plans first (API orders them first); otherwise the green-wave example (framing S-D9)
      const own = list.find((p) => !p.is_example);
      const pick = own ?? list.find((p) => p.is_example && /green-wave/i.test(p.name)) ?? list[0];
      const record = await fetchPlan(pick.id);
      dispatch({ type: 'LOAD_DB_CORRIDOR', corridor: record.plan_data, dbId: record.id, isExample: record.is_example ?? pick.is_example });
    }
  } catch {
    // Network error — stay on empty state, user can load manually
  }
}

// ============================================================================
// Provider
// ============================================================================

interface PlanProviderProps {
  children: ReactNode;
  /** Optional initial corridor to load instead of localStorage draft */
  initialCorridor?: Corridor;
  /** If true, disables editing and auto-save (for shared plan viewer) */
  readOnly?: boolean;
}

export function PlanProvider({ children, initialCorridor, readOnly = false }: PlanProviderProps) {
  // If readOnly, start with the initial corridor already loaded
  const initState: PlanState = initialCorridor
    ? {
        ...initialState,
        corridor: normalizeCorridor(initialCorridor),
        selectedIntersectionId: initialCorridor.intersections.length >= 2
          ? initialCorridor.intersections[1].id
          : null,
      }
    : initialState;

  const [state, baseDispatch] = useReducer(planReducer, initState);

  // Wrap dispatch to block mutations in readOnly mode
  const dispatch: Dispatch<PlanAction> = readOnly
    ? () => {} // No-op: block all actions in read-only mode
    : baseDispatch;

  // Load draft on mount, or auto-load last/default plan
  useEffect(() => {
    if (initialCorridor) return;
    const draft = loadDraftFromStorage();
    if (draft) {
      baseDispatch({ type: 'LOAD_CORRIDOR', corridor: draft });
      return;
    }
    autoLoadPlan(baseDispatch);
  }, [initialCorridor]);

  // Auto-save to localStorage when corridor changes (debounced, skip in readOnly mode)
  useEffect(() => {
    if (readOnly) return;
    if (!state.corridor) return;

    const timer = setTimeout(() => {
      saveDraftToStorage(state.corridor!);
    }, 1000); // 1 second debounce

    return () => clearTimeout(timer);
  }, [state.corridor, readOnly]);

  // Compute convenience values
  const activeIntersections = state.corridor
    ? getActiveIntersections(state.corridor)
    : [];
  const activePlan = state.corridor
    ? getActivePlan(state.corridor)
    : undefined;

  return (
    <PlanContext.Provider value={{ state, dispatch, activeIntersections, activePlan }}>
      {children}
    </PlanContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function usePlan(): PlanContextValue {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
}
